/* eslint-disable @typescript-eslint/no-explicit-any */
// Angular
import { BrowserModule } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import {
  NgModule,
  DoBootstrap,
  ApplicationRef,
  Injectable,
  APP_INITIALIZER,
} from '@angular/core';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HttpClient,
  HttpClientModule,
  HttpRequest,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MAT_LEGACY_PROGRESS_BAR_LOCATION } from '@angular/material/legacy-progress-bar';
import { Router } from '@angular/router';

// Sentry
import * as Sentry from '@sentry/angular';
import { ErrorHandler } from '@angular/core';

// Service
import { AuthService } from './service/auth.service';
import { OAuthService } from './service/oauth.service';
import { ConfigService } from './service/config.service';
import { RoutingService } from './service/routing.service';
import { NewsletterService } from './service/newsletter.service';
import { BannerService } from './service/ai-banner.service';

// Module
import { MaterialModule } from './module/material.module';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MomentModule } from 'ngx-moment';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { MaterialFileInputModule } from 'ngx-material-file-input';
import { NgHttpCachingModule, NgHttpCachingConfig } from 'ng-http-caching';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { simpleHash } from './shared/utils/simple-hash';
import { ResponsiveModule } from './responsive.module';
import { SharedChartsModule } from './shared/charts/shared-charts.module';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
// caching configuration
const ngHttpCachingConfig: NgHttpCachingConfig = {
  // cacheStrategy: NgHttpCachingStrategy.DISALLOW_ALL,
  lifetime: 1000 * 60 * 60,
  allowedMethod: ['GET', 'POST'],
  getKey: (req: HttpRequest<any>): string | undefined => {
    return (
      req.method +
      '@' +
      req.urlWithParams +
      '@' +
      simpleHash(req.params) +
      '@' +
      simpleHash(req.body)
    );
  },
  isCacheable: (req: HttpRequest<any>): boolean | undefined => {
    // if ( (req.urlWithParams.indexOf('customer') !== -1) || (req.urlWithParams.indexOf('newsletter') !== -1) || (req.urlWithParams.indexOf('sentry') !== -1) || ((req.urlWithParams.indexOf('/news') !== -1) && !(req.urlWithParams.indexOf('event_ids') !== -1)) ) {
    //     return false;
    // }
    if (
      req.urlWithParams.indexOf('customer') !== -1 ||
      req.urlWithParams.indexOf('reporting') !== -1 ||
      req.urlWithParams.indexOf('newsletter') !== -1 ||
      req.urlWithParams.indexOf('sentry') !== -1 ||
      (req.urlWithParams.indexOf('/news') !== -1 &&
        !(req.urlWithParams.indexOf('/news_count_itemized') !== -1) &&
        !(req.urlWithParams.indexOf('/news_item') !== -1) &&
        !(req.urlWithParams.indexOf('event_ids') !== -1))
    ) {
      return false;
    }

    return undefined;
  },
};

// Pipe
import { HighlightPipe } from './pipe/highlight.pipe';
import { NgPipesModule } from 'ngx-pipes';
import { ParagraphPipe } from './pipe/paragraph.pipe';
import { BetaPipe } from './pipe/isbeta.pipe';
import { ColorPipe } from './pipe/color.pipe';
import { ColorTrendingPipe } from './pipe/colorTrending.pipe';
import { ColorTrendingReversedPipe } from './pipe/colorTrendingReversed.pipe';
import { ColorRatioPipe } from './pipe/colorRatio.pipe';
import { ColorScorePipe } from './pipe/color-score.pipe';
import { ColorScore2Pipe } from './pipe/color-score2.pipe';
import { ThousandSuffixesPipe } from './pipe/thousand.pipe';
import { SafeHtmlPipe } from './pipe/safehtml.pipe';
import { SearchPipe } from './pipe/search.pipe';
import { SharedPipesModule } from './shared/shared-pipes.module';
import { SharedDirectivesModule } from './shared/shared-directives.module';
import { SharedComponentsModule } from './shared/shared-components.module';

// Highchart
import { ChartModule, HIGHCHARTS_MODULES } from 'angular-highcharts';
import * as more from 'highcharts/highcharts-more';
import * as highstock from 'highcharts/modules/stock';
import * as exporting from 'highcharts/modules/exporting';
import * as offline from 'highcharts/modules/offline-exporting';
import * as exportdata from 'highcharts/modules/export-data';
import * as annotations from 'highcharts/modules/annotations';
import * as map from 'highcharts/modules/map';
import { NgxChartsModule } from '@swimlane/ngx-charts';

// Admin
import { LoginComponent } from './user/login/login.component';
import { PasswordComponent } from './user/password/password.component';
import { MessageComponent } from './user/message/message.component';
import { AiBannerComponent } from './admin/aiBanner/ai-banner-component';

// Cockpit
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { CockpitComponent } from './cockpit/cockpit.component';
import { MenuFiltersComponent } from './cockpit/main-menu/menu-filters/menu-filters.component';
import { FilterPresetsListComponent } from './cockpit/main-menu/menu-filters/components/filter-presets-list/filter-presets-list.component';
import { FilterSimpleListComponent } from './cockpit/main-menu/menu-filters/components/filter-simple-list/filter-simple-list.component';
import { FilterSourcesComponent } from './cockpit/main-menu/menu-filters/components/filter-sources/filter-sources.component';
import { FilterCategoriesListComponent } from './cockpit/main-menu/menu-filters/components/filter-categories-list/filter-categories-list.component';
import { FilterEventsListComponent } from './cockpit/main-menu/menu-filters/components/filter-events-list/filter-events-list.component';
import { FilterFacttempListComponent } from './cockpit/main-menu/menu-filters/components/filter-facttemp-list/filter-facttemp-list.component';
import { NewsViewComponent } from './cockpit/main-news/news-newsfeed/news-view/news-view.component';
import { PortfolioNameComponent } from './cockpit/main-news/news-portfolio/portfolio-name/portfolio-name.component';
import { PortfolioIndexComponent } from './cockpit/main-news/news-portfolio/portfolio-index/portfolio-index.component';
import { IsinImportComponent } from './cockpit/main-news/news-portfolio/portfolio-index/isin-import/isin-import.component';
import { FileImportComponent } from './cockpit/main-news/news-portfolio/portfolio-index/file-import/file-import.component';
import { MainNewsComponent } from './cockpit/main-news/main-news.component';
import { DatepicherComponent } from './cockpit/main-menu/menu-filters/datepicher/datepicher.component';
import { MenuSearchComponent } from './cockpit/main-menu/menu-search/menu-search.component';
import { AiSearchDialogComponent } from './cockpit/main-menu/menu-search/ai-search-dialog/ai-search-dialog.component';
import { CompanyFormComponent } from './cockpit/main-menu/menu-search/ai-search-dialog/company-form/company-form.component';
import { NewsReportComponent } from './cockpit/main-news/news-newsfeed/news-report/news-report.component';
import { BsideComponent } from './cockpit/main-menu/menu-bside/menu-bside.component';
import { MenuAboveComponent } from './cockpit/main-menu/menu-above/menu-above.component';
import { MenuAsideComponent } from './cockpit/main-menu/menu-aside/menu-aside.component';
import { NewsMarketBoardComponent } from './cockpit/main-news/news-market-board/news-market-board.component';
import { NewsMarketCountriesComponent } from './cockpit/main-news/news-market-board/news-market-countries/news-market-countries.component';
import { NewsMarketOverviewComponent } from './cockpit/main-news/news-market-board/news-market-overview/news-market-overview.component';
import { MarketDetailPanelComponent } from './cockpit/main-news/news-market-board/market-detail-panel/market-detail-panel.component';
import { MenuUserTopComponent } from './cockpit/main-menu/menu-user-top/menu-user-top';
import { AnnotationComponent } from './cockpit/main-news/news-annotation/news-annotation.component';
import { NewsChartComponent } from './cockpit/main-news/news-chart/news-chart.component';
import { ChartLegendComponent } from './cockpit/main-news/news-chart/chart-legend/chart-legend.component';
import { NewsTreemapComponent } from './cockpit/main-news/news-treemap/news-treemap.component';
import { QueryTitleComponent } from './cockpit/main-news/news-title/query-title/query-title.component';
import { ChartTitleComponent } from './cockpit/main-news/news-title/chart-title/chart-title.component';
import { WidgetIframeComponent } from './project/widget/widget-iframe/widget-iframe.component';
import { NewsChartmapComponent } from './cockpit/main-news/news-chartmap/news-chartmap.component';

import { PortfolioImportComponent } from './cockpit/main-news/news-portfolio/portfolio-import/portfolio-import.component';
import { ChartmapCompanyComponent } from './cockpit/main-news/news-chartmap/chartmap-company/chartmap-company.component';
import { SentimentEventsMosaicComponent } from './cockpit/main-news/news-chartmap/chartmap-company/sentiment-events-mosaic.component';
import { ScoreEventsMosaicComponent } from './cockpit/main-news/news-chartmap/chartmap-company/score-events-mosaic.component';
import { EventRowComponent } from './cockpit/main-news/news-chartmap/chartmap-company/components/event-row/event-row.component';
import { PeergroupComparisonComponent } from './cockpit/main-news/news-chartmap/chartmap-company/peergroup-comparison.component';
import { ColorTrendPipe } from './pipe/color-trend.pipe';
import { QueryFolioComponent } from './cockpit/main-news/news-query/query-folio/query-folio.component';
import { PortfolioChartComponent } from './cockpit/main-news/news-portfolio/portfolio-chart/portfolio-chart.component';
import { QueryEditorComponent } from './cockpit/main-news/news-query/query-editor/query-editor.component';
import { NewsOutletComponent } from './cockpit/main-news/news-outlet/news-outlet.component';
import { QueryNameComponent } from './cockpit/main-news/news-query/query-name/query-name.component';
import { NewstypePipe } from './pipe/newstype.pipe';
import { ChartmapInfoComponent } from './cockpit/main-news/news-chartmap/chartmap-info/chartmap-info.component';
import { MenuOptionsComponent } from './cockpit/main-menu/menu-options/menu-options.component';
import { QueryImportComponent } from './cockpit/main-news/news-query/query-import/query-import.component';
import { QueryDeleteComponent } from './cockpit/main-news/news-query/query-delete/query-delete.component';
import { ScorelabSetupComponent } from './project/settings/scorelab-setup/scorelab-setup.component';
import { PortfolioSetupComponent } from './project/settings/portfolio-setup/portfolio-setup.component';
import { AlertsSetupComponent } from './project/settings/alerts-setup/alerts-setup.component';
import { AlertsAccordionComponent } from './project/settings/alerts-setup/alerts-accordion/alerts-accordion.component';
import { AlertsSetupDeltaComponent } from './project/settings/alerts-setup/alerts-setup-delta/alerts-setup-delta.component';
import { ScoreAlertsComponent } from './project/settings/alerts-setup/score-alerts/score-alerts.component';
import { EventAlertsComponent } from './project/settings/alerts-setup/event-alerts/event-alerts.component';
import { ReportsSettingsComponent } from './project/settings/reports-setup/reports-setting/reports-settings.component';
import { ReportsFormComponent } from './project/settings/reports-setup/reports-form/reports-form.component';
import { NewsReportboxComponent } from './cockpit/main-news/news-newsfeed/news-reportbox/news-reportbox.component';
import { PortfolioShareComponent } from './cockpit/main-news/news-portfolio/portfolio-share/portfolio-share.component';
import { MicroChartComponent } from './micro-components/charts/micro-chart/micro-chart.component';
import { DialogTableComponent } from './micro-components/dialog-table/dialog-table.component';
import { DialogReportComponent } from './micro-components/dialog-report/dialog-report.component';
import { DialogWhatsNewComponent } from './micro-components/dialog-whats-new/dialog-whats-new.component';
import { ReportOverviewComponent } from './micro-components/report-overview/report-overview.component';
import { SegmentedTabsComponent } from './micro-components/segmented-tabs/segmented-tabs.component';
import { TogglePillGroupComponent } from './micro-components/toggle-pill-group/toggle-pill-group.component';
import { GlobalFiltersBarComponent } from './cockpit/main-menu/global-filters/global-filters-bar.component';
import { TimeframeSelectorComponent } from './cockpit/main-menu/global-filters/timeframe-selector/timeframe-selector.component';
import { LanguageToggleComponent } from './cockpit/main-menu/global-filters/language-toggle/language-toggle.component';
import { ScoreButtonComponent } from './cockpit/main-menu/global-filters/score-button/score-button.component';
import { DateRangeDialogComponent } from './cockpit/main-menu/global-filters/date-range-dialog/date-range-dialog.component';
import { AiLoadingCardComponent } from './micro-components/ai-loading-card/ai-loading-card.component';
import { QueryBuilderComponent } from './micro-components/query-builder/query-builder.component';
import { QueryNodeComponent } from './micro-components/query-builder/query-node/query-node.component';
import { TermPickerComponent } from './micro-components/term-picker/term-picker.component';
import { NoticeSnackbarComponent } from './micro-components/notice-snackbar/notice-snackbar.component';
import { DueDiligenceComponent } from './cockpit/due-diligence/due-diligence.component';
import { EventAnalyticsTemporalityComponent } from './cockpit/main-news/news-chartmap/chartmap-company/components/event-analytics-temporality.component/event-analytics-temporality.component';
import { DynamicContainerComponent } from './container/dynamic-container.component';
import { ChatMobileHeaderComponent } from './project/llm/chat/chat-mobile-header/chat-mobile-header.component';
import { ChatMobileNavigationComponent } from './project/llm/chat/chat-mobile-navigation/chat-mobile-navigation.component';
import { MenuUserProfileComponent } from './cockpit/main-menu/menu-user-profile/menu-user-profile.component';
import { MobileLayoutComponent } from './project/mobile/mobile-layout/mobile-layout.component';
import { MobileMenuAboveComponent } from './project/mobile/mobile-menu-above/mobile-menu-above.component';
import { MobileSearchBarComponent } from './project/mobile/mobile-search-bar/mobile-search-bar.component';
import { MobileUserNavigationComponent } from './project/mobile/mobile-user-navigation/mobile-user-navigation.component';
import { MobileAccountSettingComponent } from './project/mobile/mobile-account-setting/mobile-account-setting.component';
import { BottomSheetWrapperComponent } from './project/mobile/shared/components/bottom-sheet-wrapper/bottom-sheet-wrapper.component';
import { MobilePortfolioSetupComponent } from './project/mobile/mobile-portfolio-setup/mobile-portfolio-setup.component';
import { NewsAuditViewComponent } from './cockpit/main-news/news-audit-view/news-audit-view.component';
import { NewsAuditTitleComponent } from './cockpit/main-news/news-audit-title/news-audit-title.component';
import { CommodityReportsComponent } from './cockpit/commodity-reports/commodity-reports.component';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

// Reads the disableServiceWorker flag from the config.json prefetched in
// main.ts (window.__APP_CONFIG__). Evaluated at module-eval time, before
// ConfigService runs, so it cannot use DI.
function isServiceWorkerDisabledByConfig(): boolean {
  const cfg = (
    window as unknown as Record<string, { disableServiceWorker?: unknown }>
  )['__APP_CONFIG__'];
  return (
    cfg?.disableServiceWorker === 'true' || cfg?.disableServiceWorker === true
  );
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}
  handleError(error) {
    Sentry.captureException(error.originalError || error);
    throw error;
  }
}

@NgModule({
  declarations: [
    AppComponent,
    CommodityReportsComponent,
    LoginComponent,
    CockpitComponent,
    MenuFiltersComponent,
    FilterPresetsListComponent,
    FilterSimpleListComponent,
    FilterSourcesComponent,
    FilterCategoriesListComponent,
    FilterEventsListComponent,
    FilterFacttempListComponent,
    PortfolioNameComponent,
    PortfolioIndexComponent,
    IsinImportComponent,
    FileImportComponent,
    MainNewsComponent,
    HighlightPipe,
    DatepicherComponent,
    MenuSearchComponent,
    AiSearchDialogComponent,
    CompanyFormComponent,
    PasswordComponent,
    MessageComponent,
    NewsReportComponent,
    BsideComponent,
    MobileLayoutComponent,
    MobileMenuAboveComponent,
    MobileSearchBarComponent,
    MobileUserNavigationComponent,
    MobileAccountSettingComponent,
    MobilePortfolioSetupComponent,
    BottomSheetWrapperComponent,
    MenuAboveComponent,
    NewsViewComponent,
    ParagraphPipe,
    MenuAsideComponent,
    NewsMarketBoardComponent,
    NewsMarketCountriesComponent,
    NewsMarketOverviewComponent,
    MarketDetailPanelComponent,
    BetaPipe,
    MenuUserTopComponent,
    ColorPipe,
    ColorTrendingPipe,
    ColorTrendingReversedPipe,
    ColorRatioPipe,
    ColorScorePipe,
    ColorScore2Pipe,
    AnnotationComponent,
    NewsChartComponent,
    ChartLegendComponent,
    NewsTreemapComponent,
    QueryTitleComponent,
    ChartTitleComponent,
    SafeHtmlPipe,
    AiBannerComponent,
    WidgetIframeComponent,
    NewsChartmapComponent,
    PortfolioImportComponent,
    ChartmapCompanyComponent,
    SentimentEventsMosaicComponent,
    ScoreEventsMosaicComponent,
    EventRowComponent,
    PeergroupComparisonComponent,
    EventAnalyticsTemporalityComponent,
    ColorTrendPipe,
    QueryFolioComponent,
    PortfolioChartComponent,
    QueryEditorComponent,
    NewsOutletComponent,
    QueryNameComponent,
    NewstypePipe,
    ChartmapInfoComponent,
    MenuOptionsComponent,
    QueryImportComponent,
    QueryDeleteComponent,
    ScorelabSetupComponent,
    PortfolioSetupComponent,
    AlertsSetupComponent,
    AlertsAccordionComponent,
    AlertsSetupDeltaComponent,
    ScoreAlertsComponent,
    EventAlertsComponent,
    ReportsSettingsComponent,
    ReportsFormComponent,
    NewsReportboxComponent,
    PortfolioShareComponent,
    MicroChartComponent,
    DialogTableComponent,
    DialogReportComponent,
    DialogWhatsNewComponent,
    ReportOverviewComponent,
    SegmentedTabsComponent,
    TogglePillGroupComponent,
    GlobalFiltersBarComponent,
    TimeframeSelectorComponent,
    LanguageToggleComponent,
    ScoreButtonComponent,
    DateRangeDialogComponent,
    AiLoadingCardComponent,
    QueryBuilderComponent,
    QueryNodeComponent,
    TermPickerComponent,
    NoticeSnackbarComponent,
    DueDiligenceComponent,
    ChatMobileNavigationComponent,
    ChatMobileHeaderComponent,
    DynamicContainerComponent,
    MenuUserProfileComponent,
    NewsAuditViewComponent,
    NewsAuditTitleComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    NgPipesModule,
    InfiniteScrollModule,
    ChartModule,
    MomentModule,
    ServiceWorkerModule.register('./ngsw-worker.js', {
      // Off in dev builds, and runtime-disablable via config.json
      // (disableServiceWorker) — e.g. behind the UBS on-prem OAuth gateway,
      // where the SW's programmatic asset fetches get redirected cross-origin
      // to the login provider and surface as CORS errors.
      enabled: environment.production && !isServiceWorkerDisabledByConfig(),
    }),
    CKEditorModule,
    NgxChartsModule,
    MatSortModule,
    MatDialogModule,
    MaterialFileInputModule,
    NgHttpCachingModule.forRoot(ngHttpCachingConfig),
    AngularSvgIconModule.forRoot(),
    ResponsiveModule,
    SharedPipesModule,
    SharedDirectivesModule,
    SharedComponentsModule,
    MatBottomSheetModule,
    SharedChartsModule,
  ],
  exports: [ResponsiveModule],
  providers: [
    {
      provide: MAT_LEGACY_PROGRESS_BAR_LOCATION,
      useValue: { getPathname: () => '' },
    },
    AuthService,
    OAuthService,
    RoutingService,
    NewsletterService,
    BannerService,
    SearchPipe,
    ColorPipe,
    ColorTrendingPipe,
    ColorTrendingReversedPipe,
    ColorRatioPipe,
    ColorScorePipe,
    ColorScore2Pipe,
    ThousandSuffixesPipe,
    {
      provide: HIGHCHARTS_MODULES,
      useFactory: () => [
        more,
        highstock,
        exporting,
        exportdata,
        offline,
        annotations,
        map,
      ],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: MAT_DIALOG_DATA,
      useValue: {},
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (platformLocation: PlatformLocation) =>
        platformLocation.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    ConfigService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService) => {
        return () => {
          return configService.loadAppConfig();
        };
      },
      deps: [ConfigService, Sentry.TraceService],
      multi: true,
    },
  ],
})
export class AppModule implements DoBootstrap {
  constructor(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    matIconRegistry.addSvgIconSet(
      domSanitizer.bypassSecurityTrustResourceUrl('src/assets/mdi.svg'),
    );
  }
  ngDoBootstrap(appRef: ApplicationRef) {
    appRef.bootstrap(AppComponent);
  }
}
